import { env } from 'env';

const isClient = typeof window !== 'undefined';

export interface DatadogLogProps {
  logMessage: string;
  condensedTags: string;
  context?: Record<string, unknown>;
}

const DATADOG_LOGS_URL = 'https://http-intake.logs.datadoghq.com/api/v2/logs';
export enum DATADOG_FLOW_TYPE {
  JOIN = 'join',
  UPGRADE = 'upgrade',
}

export enum DATADOG_API_SOURCE {
  BACKEND = 'backend',
  CONTENTFUL = 'contentful',
  STRIPE = 'stripe',
  SMARTY = 'smarty',
}

export enum DATADOG_TARGET_SERVICE {
  COMMERCE_SERVICE = 'commerce-service',
  ORDER_SERVICE = 'order-service',
  MSV2 = 'membership-service-v2',
  USER_SERVICE = 'user-service',
  WEB_CONTENTFUL = 'whoop-web-contentful',
  DOT_COM_CONTENTFUL = 'whoop-dot-com-contentful',
  STRIPE = 'stripe',
  SMARTY = 'smarty',
}

export enum DATADOG_TAGS {
  FLOW_TYPE = 'flow_type',
  API_SOURCE = 'api_source',
  SERVICE_PROVIDER = 'service_provider', // This cannot be service since its a reserved word in Datadog
  ENDPOINT = 'endpoint',
  CONTENTFUL_MODEL = 'contentful_model',
}

export type DATADOG_TAGS_TYPE = {
  [DATADOG_TAGS.FLOW_TYPE]: DATADOG_FLOW_TYPE;
  [DATADOG_TAGS.API_SOURCE]?: DATADOG_API_SOURCE;
  [DATADOG_TAGS.SERVICE_PROVIDER]?: DATADOG_TARGET_SERVICE;
  [DATADOG_TAGS.ENDPOINT]?: string;
  [DATADOG_TAGS.CONTENTFUL_MODEL]?: string;
};

export const DATADOG_TAGS_UPGRADE_BACKEND_COMMERCE_SERVICE = {
  [DATADOG_TAGS.FLOW_TYPE]: DATADOG_FLOW_TYPE.UPGRADE,
  [DATADOG_TAGS.API_SOURCE]: DATADOG_API_SOURCE.BACKEND,
  [DATADOG_TAGS.SERVICE_PROVIDER]: DATADOG_TARGET_SERVICE.COMMERCE_SERVICE,
};

export const DATADOG_TAGS_JOIN_BACKEND_COMMERCE_SERVICE = {
  [DATADOG_TAGS.FLOW_TYPE]: DATADOG_FLOW_TYPE.JOIN,
  [DATADOG_TAGS.API_SOURCE]: DATADOG_API_SOURCE.BACKEND,
  [DATADOG_TAGS.SERVICE_PROVIDER]: DATADOG_TARGET_SERVICE.COMMERCE_SERVICE,
};

/**
 * Server-side function to send logs to Datadog directly
 * @param messageOrError - Message string or Error object to log
 * @param context - Additional context data (optional)
 */
async function sendServerLogToDataDog({
  logMessage,
  condensedTags,
  context,
}: DatadogLogProps): Promise<void> {
  try {
    await fetch(DATADOG_LOGS_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'DD-API-KEY': env.DATADOG_LOGS_KEY || '',
      },
      body: JSON.stringify({
        message: logMessage,
        status: 'error',
        side: 'server',
        service: 'join-flow',
        env: env.NEXT_PUBLIC_ENV,
        ddsource: 'whoop-web',
        ddtags: condensedTags,
        timestamp: new Date().toISOString(),
        ...context,
      }),
    });
  } catch (error) {
    console.error('[Datadog Server Logger] Failed to send log:', error);
  }
}

/**
 * Client-side function to send logs to Datadog via API route
 * @param messageOrError - Message string or Error object to log
 * @param context - Additional context data (optional)
 */
async function sendClientLogToDataDog({
  logMessage,
  condensedTags,
  context,
}: DatadogLogProps): Promise<void> {
  try {
    await fetch('/api/log', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      // Sends the log message, tags, and context to the client side API route
      body: JSON.stringify({
        logMessage,
        condensedTags,
        context: {
          side: 'client',
          url: window.location.href,
          ...context,
        },
      }),
    });
  } catch (error) {
    console.error('[Datadog Client Logger] Failed to send log:', error);
  }
}

// Export the appropriate function based on the environment
// These functions are async and need to be awaited
export const sendLogToDataDog = isClient
  ? sendClientLogToDataDog
  : sendServerLogToDataDog;
